/**
 * Persists in-progress HIL retry-all state per dealer (separate from Test Nurturing).
 */

export interface PersistedHilRetryAllState {
  dealerId: string;
  automationInProgress: boolean;
  retryAllInProgress: boolean;
  jobId: string;
  progress: {
    current: number;
    total: number;
  };
  latestStatusText: string;
  latestStepLabel: string;
  timestamp: number;
}

const STORAGE_KEY_PREFIX = 'hil_segments_retry_all_state_';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function getStorageKey(dealerId: string): string {
  return `${STORAGE_KEY_PREFIX}${dealerId}`;
}

export function saveHilRetryAllState(
  dealerId: string,
  state: Omit<PersistedHilRetryAllState, 'timestamp' | 'dealerId'>,
): void {
  try {
    if (!dealerId) return;
    const persistedState: PersistedHilRetryAllState = {
      ...state,
      dealerId,
      timestamp: Date.now(),
    };
    localStorage.setItem(getStorageKey(dealerId), JSON.stringify(persistedState));
  } catch (error) {
    console.error('Failed to save HIL retry-all state:', error);
  }
}

export function loadHilRetryAllState(
  dealerId: string,
): PersistedHilRetryAllState | null {
  try {
    if (!dealerId) return null;
    const stored = localStorage.getItem(getStorageKey(dealerId));
    if (!stored) return null;

    const state: PersistedHilRetryAllState = JSON.parse(stored);
    if (
      !state ||
      state.dealerId !== dealerId ||
      typeof state.automationInProgress !== 'boolean' ||
      !state.jobId?.trim() ||
      !state.progress ||
      typeof state.progress.current !== 'number' ||
      typeof state.progress.total !== 'number'
    ) {
      clearHilRetryAllState(dealerId);
      return null;
    }

    const age = Date.now() - (state.timestamp || 0);
    if (age > MAX_AGE_MS) {
      clearHilRetryAllState(dealerId);
      return null;
    }

    if (!state.automationInProgress) {
      return null;
    }

    return state;
  } catch (error) {
    console.error('Failed to load HIL retry-all state:', error);
    return null;
  }
}

export function clearHilRetryAllState(dealerId: string): void {
  try {
    if (!dealerId) return;
    localStorage.removeItem(getStorageKey(dealerId));
  } catch (error) {
    console.error('Failed to clear HIL retry-all state:', error);
  }
}

export function updateHilRetryAllLiveStatus(
  dealerId: string,
  patch: {
    progress?: { current: number; total: number };
    latestStatusText?: string;
    latestStepLabel?: string;
  },
): void {
  try {
    if (!dealerId) return;
    const existing = loadHilRetryAllState(dealerId);
    if (!existing) return;
    saveHilRetryAllState(dealerId, {
      automationInProgress: existing.automationInProgress,
      retryAllInProgress: existing.retryAllInProgress,
      jobId: existing.jobId,
      progress: patch.progress ?? existing.progress,
      latestStatusText: patch.latestStatusText ?? existing.latestStatusText,
      latestStepLabel: patch.latestStepLabel ?? existing.latestStepLabel,
    });
  } catch (error) {
    console.error('Failed to update HIL retry-all live status:', error);
  }
}
